/*
 * Copyright (c) 2021 LucidResolutions LLC. All rights reserved.
 */

import React, {useContext, useEffect, useState} from "react";
import {Badge, Button, Card, Col, Divider, Form, Input, notification, Result, Row, Steps} from 'antd';
import {
    CheckOutlined,
    DollarOutlined,
    LoadingOutlined,
    LockOutlined,
    SolutionOutlined,
    UserOutlined
} from '@ant-design/icons';
import {useHistory} from "react-router-dom";
import AuthService from "../../service/auth.service";
import UserService from "../../service/user.service";
import {AuthContext} from "../../context/auth.context";
import PaymentService from "../../service/payment.service";
import {IfEqual, IfTruthy} from "../../component/if.equal.component";
import {useWindowSize} from "../../hooks/windowSize";

const Step = Steps.Step

let paymentSVC = new PaymentService()
let authSVC = new AuthService()

const Register = () => {

    let authContext = useContext(AuthContext)
    let userSVC = new UserService(authSVC, authContext)
    let [email, setEmail] = useState('')
    let [password, setPassword] = useState('')
    let [currentStep, setCurrentStep] = useState(0)
    const [paymentVerified, setPaymentVerified] = useState(false)
    const windowSize: any = useWindowSize()
    const [isSmall, setIsSmall] = useState(false)
    const [form] =  Form.useForm()
    const [registering, setRegistering] = useState(false)
    const [stripeButtons, setStripeButtons] = useState([])
    const [refId, setRefId] = useState('')
    const [paymentAccepted, setPaymentAccepted] = useState(false)
    const [focused, setFocused] = useState(false)
    const [blurred, setBlurred] = useState(false)

    useEffect(() => {
        const onFocus = ()=> setFocused(true)
        const onBlur = ()=> setBlurred(true)
        window.addEventListener("focus", onFocus);
        window.addEventListener("blur", onBlur);

        return () => {
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("blur", onBlur);
        };
    }, []);


    useEffect(()=>{
        paymentSVC.buttons().then((buttons: any)=>{
            setStripeButtons(buttons)
            if(buttons?.length){
                setRefId(buttons[0].reference_id)
            }
        })

    }, [])

    useEffect(()=>{
        setIsSmall(windowSize?.width < 600)
    }, [windowSize.width])

    const onChange = (current: number)=>{
        setCurrentStep(current)
    }



    const register = async (license: any) =>{
        setRegistering(true)
        try{
            await userSVC.register(email, password, license)
            setRegistering(false)
        }catch (e){
            setRegistering(false)
            showGenericError()
            onChange(0)
            return
        }

        setPaymentVerified(true)
        onChange(2)
    }

    useEffect(() => {
        let id = setInterval(()=>{
            if(currentStep != 1 || !refId ){
                return
            }

            paymentSVC.checkOutStatus(refId).then(()=>{
                clearInterval(id);
                setPaymentAccepted(true)
                return register(refId)
            }).catch(()=>{

            })


        }, 3000)

        return ()=> clearInterval(id)
    }, [currentStep, refId, register]);

    const showGenericError = ()=>{
        notification.error({
            message: "An error occurred",
            duration: 0,
            description: "If issue persists please contact support@lucidtactics.com",
            placement: "topLeft"
        })
    }

    const verifyUser = ()=>{
        userSVC.verify(email)
            .then(()=>{
                onChange(1)
            })
            .catch((e)=>{
            if(e.status == 422){
                notification.error({
                    message: "Already Registered",
                    duration: 0,
                    description: "The email you entered is already registered. Please login to continue",
                    placement: "topLeft"
                })
                return
            }

            showGenericError()
        })
    }

    const history = useHistory()
    const goToConsole = async () => {
        try{
            let info = await userSVC.login(email, password)
            authContext.setToken(info.token)
            history.replace("/", {newExchange: true})
        }catch (e){
            console.error(e)
            showGenericError()
        }
    };

    return (
        <Row>
            <Col xs={{span: 24}} lg={{span: 20, offset: 2}} className={'registration'}>
                <Badge.Ribbon placement={'start'} text="Register">
                    <Card style={{paddingTop:40}} >
                        <Steps
                            current={currentStep}
                            type={'navigation'}
                            onChange={onChange}
                        >
                            <Step title={isSmall ? undefined : "Registration"} icon={<SolutionOutlined />} disabled={paymentAccepted}/>


                            <Step title={isSmall ? undefined :"Payment"} icon={<DollarOutlined />} disabled={currentStep == 2}/>

                            <Step title={isSmall ? undefined :"Access"} icon={<CheckOutlined />} disabled={currentStep!=2}/>
                        </Steps>
                        <br/>
                        <IfEqual expect={0} actual={currentStep}>
                            <Form
                                size={'large'}
                                form={form}
                                // name="normal_login"
                                // className="login-form"
                                initialValues={{ email: email, password: password }}
                                onFinish={()=>{
                                    verifyUser()
                                }}
                            >
                                <Form.Item
                                    name="email"
                                    rules={[
                                        {
                                            type: 'email',
                                            message: 'The input is not valid E-mail!',
                                        },
                                        { required: true, message: 'Please input your Email!' }]}
                                >
                                    <Input prefix={<UserOutlined className="site-form-item-icon" />}
                                           autoComplete="new-password"
                                           onChange={(e)=>{
                                               setEmail(e.target.value)
                                           }}
                                           placeholder="Email" />
                                </Form.Item>
                                <Form.Item
                                    name="password"
                                    rules={[{ required: true, message: 'Please input your Password!' }]}
                                >
                                    <Input
                                        prefix={<LockOutlined className="site-form-item-icon" />}
                                        type="password"
                                        placeholder="Password"
                                        autoComplete="new-password"
                                        onChange={(e)=>{
                                            setPassword(e.target.value)
                                        }}
                                    />
                                </Form.Item>
                            </Form>
                        </IfEqual>

                        <IfEqual expect={1} actual={currentStep}>
                            <div className={'centered'}>
                                <IfTruthy val={!paymentAccepted}>
                                {stripeButtons.map((b: any)=> {

                                    // @ts-ignore
                                    return <stripe-buy-button
                                        key={b.id}
                                        style={{marginRight: 10}}
                                        buy-button-id={b.id}
                                        publishable-key={b.key}
                                        client-reference-id={b.reference_id}
                                        customer-email={email}
                                    />
                                })}

                                    <IfTruthy val={blurred && focused}>
                                        <hr/>
                                        <Result
                                            status={404}
                                            title={"Already Paid?"}
                                            subTitle={<>
                                                If you just paid please wait a few seconds for it to be detected <br/>
                                                If more than 10 seconds goes by please contact us at
                                                support@lucidtactics.com
                                            </>}
                                        />
                                    </IfTruthy>

                                </IfTruthy>
                                <IfTruthy val={paymentAccepted}>
                                    <Result
                                        icon={<LoadingOutlined/>}
                                        title="Payment Received"
                                        subTitle="Registering...."
                                    />
                                </IfTruthy>
                            </div>

                        </IfEqual>

                        <IfEqual expect={2} actual={currentStep}>
                            <Result
                                status="success"
                                title="Registration Complete"
                                extra={[
                                    <Button onClick={goToConsole} type="primary" key="console">
                                        Go Console
                                    </Button>,
                                ]}
                            />
                        </IfEqual>
                        <br/>
                        <IfEqual expect={0} actual={currentStep}>
                            <Button type="primary" disabled={!(email && password)} onClick={()=>{
                                form.submit()
                            }}>Next</Button>
                        </IfEqual>

                        <Divider/>
                        <div className={'centered'}>
                            By registering you agree
                            to our <a rel='noreferrer' target='_blank' href='https://lucidtactics.com/terms-conditions'>
                            Terms & Conditions</a> and our <a rel='noreferrer' target='_blank' href='https://lucidtactics.com/privacy-policy'>Privacy Policy</a>
                        </div>
                </Card>
                </Badge.Ribbon>
            </Col>
        </Row>

    );
};

export {Register}
